// Validação partilhada dos formulários públicos (Contactos, Agendamento,
// Orçamento, Diagnóstico). Devolve sempre { field: mensagem } para os campos
// inválidos e { isValid, errors, firstErrorField }.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Aceita: 9 dígitos PT (9XXXXXXXX), com/sem +351, espaços, hífens e parênteses
const stripPhone = (v) => (v || "").replace(/[\s().+-]/g, "").replace(/^351/, "");
const PHONE_RE = /^[0-9]{9}$/;

// Código postal PT: XXXX-XXX
const POSTAL_RE = /^\d{4}-\d{3}$/;

export const isValidEmail = (v) => EMAIL_RE.test((v || "").trim());
export const isValidName = (v) => (v || "").trim().length >= 2;
export const isValidPhonePT = (v) => PHONE_RE.test(stripPhone(v));
export const isValidPostalPT = (v) => POSTAL_RE.test((v || "").trim());

// Auto-formata o código postal enquanto o utilizador escreve.
// Aceita apenas dígitos, insere o hífen depois do 4.º dígito, corta em 7.
export const formatPostalPT = (raw) => {
  const digits = (raw || "").replace(/\D/g, "").slice(0, 7);
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)}-${digits.slice(4)}`;
};

// Auto-formata o telefone enquanto escreve.
// Regras PT: aceita apenas 9 dígitos (ex.: 910 000 000). Se o utilizador colar
// com prefixo +351 ou 00351, remove-o silenciosamente. Insere espaço de 3 em 3.
export const formatPhonePT = (raw) => {
  if (!raw) return "";
  let s = (raw + "").replace(/\D/g, "");
  // Remove prefixo internacional PT.
  // • "00351" no início → 5 dígitos de prefixo (pode aparecer se colar internacional cheio).
  // • "351" no início E o total tem mais de 9 dígitos → significa que o utilizador
  //   escreveu (ou colou) o prefixo 351 seguido do número nacional. Sem a
  //   verificação "> 9", alguém a digitar "351" progressivamente veria o campo
  //   apagar-se. Ao passar dos 9 dígitos, é seguro assumir prefixo internacional
  //   e descartar os 3 primeiros.
  if (s.startsWith("00351")) s = s.slice(5);
  else if (s.startsWith("351") && s.length > 9) s = s.slice(3);
  s = s.slice(0, 9);
  return s.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
};

/**
 * Valida bloco de identidade (nome/email/telefone e opcionalmente CP).
 * @param {object} values
 * @param {object} opts - { phoneRequired=true, postalRequired=false }
 * @returns {{isValid:boolean, errors:object, firstErrorField:string|null}}
 */
export const validateIdentity = (values = {}, opts = {}) => {
  const { phoneRequired = true, postalRequired = false } = opts;
  const errors = {};

  if (!isValidName(values.nome)) {
    errors.nome = "Indique o seu nome (mínimo 2 caracteres).";
  }
  if (!(values.email || "").trim()) {
    errors.email = "O email é obrigatório.";
  } else if (!isValidEmail(values.email)) {
    errors.email = "Introduza um email válido (ex.: nome@dominio.pt).";
  }
  if (phoneRequired) {
    if (!(values.telefone || "").trim()) {
      errors.telefone = "O telefone é obrigatório.";
    } else if (!isValidPhonePT(values.telefone)) {
      errors.telefone = "Telefone inválido (9 dígitos).";
    }
  } else if ((values.telefone || "").trim() && !isValidPhonePT(values.telefone)) {
    errors.telefone = "Telefone inválido (9 dígitos).";
  }
  if (postalRequired) {
    if (!(values.codigo_postal || "").trim()) {
      errors.codigo_postal = "O código postal é obrigatório.";
    } else if (!isValidPostalPT(values.codigo_postal)) {
      errors.codigo_postal = "Formato inválido (XXXX-XXX).";
    }
  }

  const firstErrorField = Object.keys(errors)[0] || null;
  return { isValid: Object.keys(errors).length === 0, errors, firstErrorField };
};

/**
 * Faz focus no primeiro campo com erro. `prefix` é o mesmo usado nos ids
 * (ex.: "agd" resulta em id="agd-nome", "agd-email" ...). Mapeamento:
 *   nome           -> `${prefix}-nome`
 *   email          -> `${prefix}-email`
 *   telefone       -> `${prefix}-telefone`
 *   codigo_postal  -> `${prefix}-cp`
 *   mensagem       -> `${prefix}-mensagem`
 *   assunto        -> `${prefix}-assunto`
 */
export const focusFirstError = (firstErrorField, prefix) => {
  if (!firstErrorField) return;
  const map = {
    nome: `${prefix}-nome`,
    email: `${prefix}-email`,
    telefone: `${prefix}-telefone`,
    codigo_postal: `${prefix}-cp`,
    mensagem: `${prefix}-mensagem`,
    assunto: `${prefix}-assunto`,
  };
  const elId = map[firstErrorField] || `${prefix}-${firstErrorField}`;
  requestAnimationFrame(() => {
    const el = document.getElementById(elId);
    if (el && typeof el.focus === "function") {
      el.focus({ preventScroll: false });
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch (_) {
        /* older browsers */
      }
    }
  });
};

// Constrói mensagem para toast: singular vs plural
export const buildErrorSummary = (errors) => {
  const count = Object.keys(errors).length;
  if (count === 0) return "";
  if (count === 1) return "Corrija o campo assinalado antes de continuar.";
  return `Corrija os ${count} campos assinalados antes de continuar.`;
};
